import {
  Badge,
  Box,
  Flex,
  Icon,
  IconButton,
  Input,
  Text,
} from '@chakra-ui/react'
import { ListFilter } from 'lucide-react'
import { memo, useCallback, useMemo } from 'react'

import { ListToolbar } from '@mangovoice/mango-ui'
import type { TranslateFn } from '@mangovoice/mango-ui'

import { useExtensionsI18n } from '../i18n/provider'

export const EXTENSION_PAGE_SIZES = [25, 50, 100, 250, 500] as const
export type ExtensionPageSize = (typeof EXTENSION_PAGE_SIZES)[number] | 'All'

const TOOLBAR_PAGE_SIZE_OPTIONS: number[] = [...EXTENSION_PAGE_SIZES]

interface ExtensionsListToolbarProps {
  searchValue: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string
  pageSize: ExtensionPageSize
  onPageSizeChange: (size: ExtensionPageSize) => void
  currentPage: number
  totalCount: number
  onPageChange: (page: number) => void
  isLoading: boolean
  showSearch?: boolean
  showPagingControls?: boolean
  showFilterButton?: boolean
  filtersActive?: boolean
  activeFilterCount?: number
  onToggleFilters?: () => void
}

const TOOLBAR_FALLBACKS: Record<string, string> = {
  'extensions.pagination.resultsPerPage': 'Results per page:',
  'extensions.pagination.all': 'All',
  'extensions.pagination.previousPage': 'Previous page',
  'extensions.pagination.nextPage': 'Next page',
  'extensions.pagination.jumpToPage': 'Jump to page {{page}}',
}

export const ExtensionsListToolbar = memo<ExtensionsListToolbarProps>(
  ({
    searchValue,
    onSearchChange,
    searchPlaceholder,
    pageSize,
    onPageSizeChange,
    currentPage,
    totalCount,
    onPageChange,
    isLoading,
    showSearch = true,
    showPagingControls = true,
    showFilterButton = false,
    filtersActive = false,
    activeFilterCount = 0,
    onToggleFilters,
  }) => {
    const { text } = useExtensionsI18n()
    const resolvedSearchPlaceholder =
      searchPlaceholder ??
      text('extensions.list.searchPlaceholder', 'Search by name or number')
    const t: TranslateFn = useCallback(
      (key, opts) =>
        text(
          key,
          TOOLBAR_FALLBACKS[key] ?? key,
          opts as Record<string, string | number> | undefined,
        ),
      [text],
    )
    const rangeLabel = useMemo(() => {
      if (totalCount <= 0) return null
      if (pageSize === 'All') {
        return text(
          'extensions.pagination.showingAll',
          'Showing all {{total}} results',
          { total: totalCount },
        )
      }
      const start = (currentPage - 1) * pageSize + 1
      const end = Math.min(currentPage * pageSize, totalCount)
      return text(
        'extensions.pagination.showingRange',
        'Showing {{start}} to {{end}} of {{total}} results',
        { start, end, total: totalCount },
      )
    }, [currentPage, pageSize, text, totalCount])

    return (
      <Box
        display="flex"
        flexWrap="wrap"
        gap={4}
        alignItems="center"
        py={4}
        borderTopWidth="1px"
        borderColor="border.default"
      >
        {showSearch && (
          <Flex align="center" gap={0}>
            <Box w="280px" position="relative">
              <Icon
                boxSize={4}
                color="fg.muted"
                position="absolute"
                left={3}
                top="50%"
                transform="translateY(-50%)"
                zIndex={1}
                pointerEvents="none"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </Icon>
              <Input
                placeholder={resolvedSearchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                size="sm"
                pl={10}
                bg="bg.subtle"
                borderColor="border.default"
                borderRightRadius={showFilterButton ? 0 : undefined}
                _placeholder={{ color: 'fg.subtle' }}
                _hover={{ borderColor: 'border.emphasized' }}
                _focus={{
                  borderColor: 'brand.solid',
                  boxShadow: '0 0 0 1px var(--chakra-colors-brand-solid)',
                }}
                aria-label={text(
                  'extensions.pagination.searchExtensions',
                  'Search extensions',
                )}
              />
            </Box>
            {showFilterButton && (
              <Box position="relative">
                <IconButton
                  aria-label={text(
                    'extensions.pagination.toggleFilters',
                    'Toggle filters',
                  )}
                  size="sm"
                  variant={filtersActive ? 'solid' : 'outline'}
                  colorPalette={filtersActive ? 'brand' : undefined}
                  onClick={onToggleFilters}
                  borderLeftRadius={0}
                  borderLeftWidth={0}
                >
                  <Icon
                    as={ListFilter}
                    boxSize={4}
                    color={
                      filtersActive ? 'colorPalette.contrast' : 'fg.default'
                    }
                  />
                </IconButton>
                {activeFilterCount > 0 && (
                  <Badge
                    position="absolute"
                    top="-1"
                    right="-1"
                    size="xs"
                    colorPalette="info"
                    borderRadius="full"
                    minW={4}
                    h={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="2xs"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Box>
            )}
          </Flex>
        )}
        {showPagingControls && (
          <Box
            flex="1"
            minW="240px"
            css={{ '& > div': { borderTopWidth: 0, paddingTop: 0 } }}
          >
            <ListToolbar
              pageSize={pageSize}
              onPageSizeChange={(size) =>
                onPageSizeChange(size as ExtensionPageSize)
              }
              currentPage={currentPage}
              totalCount={totalCount}
              onPageChange={onPageChange}
              isLoading={isLoading}
              showToolbar
              pageSizeOptions={TOOLBAR_PAGE_SIZE_OPTIONS}
              showAllOption
              maxAllThreshold={Number.MAX_SAFE_INTEGER}
              t={t}
              i18nPrefix="extensions.pagination"
              summary={
                rangeLabel ? (
                  <Text fontSize="sm" color="fg.subtle" whiteSpace="nowrap">
                    {rangeLabel}
                  </Text>
                ) : null
              }
            />
          </Box>
        )}
      </Box>
    )
  },
)

ExtensionsListToolbar.displayName = 'ExtensionsListToolbar'
